import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import {
    CardBody,
    CardButton,
    CardImage,
    CardImageLayout,
    Description,
    DialogBody,
    DialogHeader,
    Heading,
    IconTile,
    LoadingPanel,
} from '@gitbook/spine-react';

import { apiQuery } from '../../../state';
import { IntegrationContentSourceIcon } from '../../Integrations';

export interface PickIntegrationContentSourceFlowProps {
    /** Organization in which the flow is being done */
    organizationId: string;
    /** Title for the flow */
    title?: string;
    /** Description for the flow */
    description?: string;
    /** Callback when the configuration is done. */
    onSubmit: (selected: {
        integration: api.Integration;
        contentSource: api.IntegrationContentSource;
    }) => void;
}

/**
 * Flow inside a modal to configure a specific content source.
 */
export function PickIntegrationContentSourceFlow(props: PickIntegrationContentSourceFlowProps) {
    const {
        onSubmit,
        title = 'Select a content source',
        description = 'Configure a source for a computed content.',
        organizationId,
    } = props;

    return (
        <>
            <DialogHeader>
                <Heading>{title}</Heading>
                {description ? <Description>{description}</Description> : null}
            </DialogHeader>
            <DialogBody>
                <React.Suspense fallback={<LoadingPanel size="large" />}>
                    <ListIntegrationContentSources
                        organizationId={organizationId}
                        onSubmit={onSubmit}
                    />
                </React.Suspense>
            </DialogBody>
        </>
    );
}

function ListIntegrationContentSources(
    props: Pick<PickIntegrationContentSourceFlowProps, 'onSubmit' | 'organizationId'>
) {
    const { onSubmit, organizationId } = props;

    const {
        data: { items: integrations },
    } = useSuspenseQuery(
        apiQuery('listOrganizationIntegrations', {
            organizationId,
            limit: 1000,
            contentSources: true,
        })
    );

    const contentSources = React.useMemo(() => {
        return integrations.flatMap((integration) =>
            (integration.contentSources ?? []).map((contentSource) => ({
                integration,
                contentSource,
            }))
        );
    }, [integrations]);

    return (
        <div className="flex flex-col gap-4">
            {contentSources.map(({ integration, contentSource }) => (
                <CardButton
                    key={`${integration.name}-${contentSource.id}`}
                    onPress={() => {
                        onSubmit({ integration, contentSource });
                    }}
                >
                    <CardImageLayout>
                        <CardImage>
                            <IconTile size={48} className="bg-white">
                                <IntegrationContentSourceIcon
                                    integration={integration}
                                    contentSource={contentSource}
                                    className="size-full"
                                />
                            </IconTile>
                        </CardImage>
                        <CardBody>
                            <Heading>{contentSource.title}</Heading>
                            {contentSource.description ? (
                                <Description>{contentSource.description}</Description>
                            ) : null}
                        </CardBody>
                    </CardImageLayout>
                </CardButton>
            ))}
        </div>
    );
}
